import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, CreditCard, Globe } from "lucide-react";
import DomainUsageWidget from "./DomainUsageWidget";
import WebsiteHostingTab from "./WebsiteHostingTab";
import IntervalToggle from "@/pages/landing/IntervalToggle";

const FALLBACK_INTERVALS = [
  { id: "monthly",  label: "Monthly",   period_months: 1,  badge: null },
  { id: "biannual", label: "Bi-Annual", period_months: 6,  badge: null },
  { id: "annual",   label: "Annual",    period_months: 12, badge: "Save 25%" },
];

function priceLabel(p, intervalId) {
  const pr = p?.pricing?.[intervalId];
  if (!pr) return null;
  const suffix = intervalId === "monthly" ? "/mo"
                : intervalId === "biannual" ? "/ 6 mo"
                : "/year";
  return { amount: pr.amount_usd, suffix, meta: pr };
}

export default function BillingTab({ plan }) {
  const [creating, setCreating] = useState(null);
  const [plans, setPlans] = useState([]);
  const [intervals, setIntervals] = useState(FALLBACK_INTERVALS);
  const [intervalId, setIntervalId] = useState("annual");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans(data.plans || []);
        if (Array.isArray(data.intervals) && data.intervals.length) {
          setIntervals(data.intervals);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const upgrade = async (planId) => {
    setCreating(planId);
    try {
      // Phase B — real Stripe subscription checkout using catalog Price ID.
      const { data } = await api.post(
        "/billing/subscription/checkout",
        { plan_id: planId, interval: intervalId, origin_url: window.location.origin },
      );
      window.location.href = data.url;
    } catch (e) {
      // 409 = already has an active sub → use /change; fall back to legacy checkout otherwise.
      if (e?.response?.status === 409) {
        try {
          await api.post("/billing/subscription/change",
            { plan_id: planId, interval: intervalId });
          toast.success("Plan updated");
          setCreating(null);
          window.location.reload();
          return;
        } catch { /* fall through */ }
      }
      toast.error("Couldn't start checkout");
      setCreating(null);
    }
  };

  const currentId = plan?.plan_id || "free";

  return (
    <div>
      <div className="nb-card p-5 mb-6">
        <div className="font-display text-2xl font-extrabold flex items-center gap-2"><CreditCard className="w-6 h-6" /> Plans & Billing</div>
        <div className="text-sm text-gray-600 mt-1">
          You&apos;re currently on <b>{plan?.plan_name || "Free"}</b>
          {plan?.is_paid && plan?.plan_until && ` — renews ${new Date(plan.plan_until).toLocaleDateString()}`}
          {!plan?.is_paid && plan?.free_ends_at && ` — expires ${new Date(plan.free_ends_at).toLocaleDateString()}`}.
        </div>
      </div>

      <DomainUsageWidget compact />

      <div className="my-6">
        <WebsiteHostingTab />
      </div>

      <div className="flex justify-center mb-5">
        <IntervalToggle
          intervals={intervals}
          value={intervalId}
          onChange={setIntervalId}
          testidPrefix="billing-interval"
        />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((p) => {
          const isCurrent = p.id === currentId;
          const dark = p.id === "growth";
          const isFree = p.id === "free";
          const price = isFree ? null : priceLabel(p, intervalId);
          const showAnnualDiscount = price?.meta?.reference_amount_usd && price?.meta?.discount_percent;
          const includedDomain = !!price?.meta?.free_domain_first_site;
          const nonRefundable = !!price?.meta?.non_refundable;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`nb-card p-5 flex flex-col relative ${dark ? "" : isCurrent ? "bg-[#F0FFF4]" : "bg-white"}`}
              style={dark ? { color: "#ffffff", background: "#111111" } : isCurrent ? { background: "#F0FFF4" } : {}}>
              {p.popular && (
                <div className="absolute -top-3 left-5 bg-[#FF7F50] text-white text-xs font-extrabold px-3 py-1 rounded-full border-2 border-black">
                  POPULAR
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 right-5 bg-[#10B981] text-white text-xs font-extrabold px-3 py-1 rounded-full border-2 border-black">
                  CURRENT
                </div>
              )}
              {showAnnualDiscount && !isCurrent && (
                <div className="absolute -top-3 right-5 bg-amber-400 text-amber-950 text-xs font-extrabold px-3 py-1 rounded-full border-2 border-black"
                     data-testid={`billing-save-${p.id}`}>
                  Save {price.meta.discount_percent}%
                </div>
              )}
              <div className="font-display text-xl font-extrabold" style={dark ? { color: "#ffffff" } : {}}>{p.name}</div>
              <div className="text-xs mt-1" style={{ color: dark ? "#D1D5DB" : "#4B5563" }}>{p.tagline}</div>

              {isFree ? (
                <div className="mt-3 flex items-end gap-1">
                  <div className="font-display text-3xl font-extrabold" style={dark ? { color: "#ffffff" } : {}}>$0</div>
                  <div className="pb-1 text-sm" style={{ color: dark ? "#D1D5DB" : "#4B5563" }}>/mo</div>
                </div>
              ) : (
                <>
                  {showAnnualDiscount && (
                    <div className="mt-3 text-xs line-through leading-none" style={{ color: dark ? "#9CA3AF" : "#94A3B8" }}
                         data-testid={`billing-ref-${p.id}`}>
                      ${price.meta.reference_amount_usd}/year
                    </div>
                  )}
                  <div className="mt-1 flex items-end gap-1" data-testid={`billing-amount-${p.id}`}>
                    <div className="font-display text-3xl font-extrabold" style={dark ? { color: "#ffffff" } : {}}>${price.amount}</div>
                    <div className="pb-1 text-sm" style={{ color: dark ? "#D1D5DB" : "#4B5563" }}>{price.suffix}</div>
                  </div>
                  {intervalId === "annual" && (
                    <div className="text-[11px] mt-0.5" style={{ color: dark ? "#D1D5DB" : "#64748B" }}
                         data-testid={`billing-eqmo-${p.id}`}>
                      ≈ ${price.meta.equivalent_monthly_usd}/mo billed annually
                    </div>
                  )}
                  {intervalId === "biannual" && (
                    <div className="text-[11px] mt-0.5" style={{ color: dark ? "#D1D5DB" : "#64748B" }}>
                      Billed every 6 months
                    </div>
                  )}
                </>
              )}

              {includedDomain && (
                <div className="mt-3 inline-flex items-center gap-1.5 self-start text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1"
                     data-testid={`billing-domain-${p.id}`}>
                  <Globe className="w-3 h-3" /> Free standard domain included
                </div>
              )}
              {nonRefundable && (
                <div className="mt-2 text-[10px]" style={{ color: dark ? "#9CA3AF" : "#94A3B8" }}
                     data-testid={`billing-non-refundable-${p.id}`}>
                  Annual plans are non-refundable.
                </div>
              )}

              <div className="mt-4 space-y-1.5 text-sm flex-1">
                {(p.features || []).map((f, i) => (
                  <div key={i} className="flex items-start gap-2" style={dark ? { color: "#ffffff" } : { color: "#111" }}>
                    <Check className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" strokeWidth={3} /><span>{f}</span>
                  </div>
                ))}
                {(p.missing || []).map((m, i) => (
                  <div key={`m-${i}`} className="flex items-start gap-2 line-through" style={{ color: dark ? "#6B7280" : "#9CA3AF" }}>
                    <span className="w-4 mt-0.5 shrink-0">·</span><span>{m}</span>
                  </div>
                ))}
              </div>

              {p.id === "free" ? (
                <button disabled data-testid={`btn-plan-${p.id}`}
                  className="mt-5 py-2.5 rounded-xl font-bold border-2 border-black bg-gray-200 opacity-60">
                  {isCurrent ? "Current plan" : "Included on signup"}
                </button>
              ) : (
                <button onClick={() => upgrade(p.id)} disabled={creating === p.id || isCurrent}
                  data-testid={`btn-plan-${p.id}`}
                  className={`mt-5 py-2.5 rounded-xl font-bold border-2 border-black nb-press ${dark ? "bg-[#FF7F50] text-white" : "bg-[#8B5CF6] text-white"}`}
                  style={dark ? { boxShadow: "3px 3px 0 0 #FFFFFF" } : { boxShadow: "3px 3px 0 0 #111111" }}>
                  {creating === p.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isCurrent ? "Active" : currentId === "free" ? "Upgrade" : "Switch"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="nb-card-flat p-4 mt-6 text-sm bg-[#FFF7E6]">
        <b>Domains & hosting.</b> Bi-Annual and Annual paid plans include <b>one</b> free standard first-site domain (up to $25/year registrar cost — .ai/.io and premium domains excluded).
        Website&nbsp;#1 hosting is included with your active paid plan; each additional live website is <b>$50/month Additional Website Hosting</b>.
      </div>
      <p className="text-xs text-gray-500 mt-4">Secure payment powered by Stripe.</p>
    </div>
  );
}
